// src/Services/statisticsService.js
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";

class StatisticsService {
  constructor() {
    this.playerSessionMap = new Map(); // Track unique players per day
    this.statsCache = new Map(); // Cache for statistics
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes cache TTL
  }

  // Clear cache entry
  clearCache(key) {
    this.statsCache.delete(key);
  }

  // Get cached data if valid
  getCachedData(key) {
    const cached = this.statsCache.get(key);
    if (cached) {
      const ttl = cached.ttl || this.cacheTTL;
      if (Date.now() - cached.timestamp < ttl) {
        return cached.data;
      }
    }
    return null;
  }

  // Set cache data
  setCachedData(key, data, customTTL = null) {
    this.statsCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: customTTL
    });
  }

  // Get or create daily stats document
  async getOrCreateDailyStats(date) {
    const docRef = doc(db, "dailyStats", date);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      const initialData = {
        date: date,
        gamesCreated: 0,
        gamesCompleted: 0,
        gamesAbandoned: 0,
        uniquePlayers: 0,
        playerIds: [],
        peakConcurrentGames: 0,
        currentConcurrentGames: 0,
        avgGameDuration: 0,
        totalGameDuration: 0,
        customCategoryGames: 0,
        defaultCategoryGames: 0,
        hourlyBreakdown: {},
        popularCategories: {},
        lastUpdated: serverTimestamp(),
      };

      // Initialize hourly breakdown
      for (let i = 0; i < 24; i++) {
        initialData.hourlyBreakdown[i] = { games: 0, players: 0 };
      }

      await setDoc(docRef, initialData, { merge: true });
      return initialData;
    }

    return docSnap.data();
  }

  // Track when a game is created
  async trackGameCreated(roomData) {
    try {
      const today = new Date().toISOString().split("T")[0];
      const hour = new Date().getHours();

      // Use transaction for accurate concurrent game tracking
      await runTransaction(db, async (transaction) => {
        const dailyRef = doc(db, "dailyStats", today);
        const dailyDoc = await transaction.get(dailyRef);

        let dailyData;
        if (!dailyDoc.exists()) {
          // Create initial data structure
          dailyData = {
            date: today,
            gamesCreated: 0,
            gamesCompleted: 0,
            gamesAbandoned: 0,
            uniquePlayers: 0,
            playerIds: [],
            peakConcurrentGames: 0,
            currentConcurrentGames: 0,
            avgGameDuration: 0,
            totalGameDuration: 0,
            customCategoryGames: 0,
            defaultCategoryGames: 0,
            hourlyBreakdown: {},
            popularCategories: {},
            lastUpdated: serverTimestamp(),
          };

          // Initialize hourly breakdown
          for (let i = 0; i < 24; i++) {
            dailyData.hourlyBreakdown[i] = { games: 0, players: 0 };
          }
        } else {
          dailyData = dailyDoc.data();
        }

        const newConcurrent = (dailyData.currentConcurrentGames || 0) + 1;
        const defaultCategories = ["name", "animal", "place", "thing"];
        const roomCategories = (roomData.categories || []).map(cat => cat.toLowerCase().trim());
        const isCustom = roomCategories.length !== 4 ||
          !defaultCategories.every(cat => roomCategories.includes(cat));

        // Update the data
        const updates = {
          date: today,
          gamesCreated: (dailyData.gamesCreated || 0) + 1,
          currentConcurrentGames: newConcurrent,
          peakConcurrentGames: Math.max(
            dailyData.peakConcurrentGames || 0,
            newConcurrent
          ),
          customCategoryGames:
            (dailyData.customCategoryGames || 0) + (isCustom ? 1 : 0),
          defaultCategoryGames:
            (dailyData.defaultCategoryGames || 0) + (!isCustom ? 1 : 0),
          [`hourlyBreakdown.${hour}.games`]:
            (dailyData.hourlyBreakdown?.[hour]?.games || 0) + 1,
          lastUpdated: serverTimestamp(),
          // Ensure all required fields exist
          gamesCompleted: dailyData.gamesCompleted || 0,
          gamesAbandoned: dailyData.gamesAbandoned || 0,
          uniquePlayers: dailyData.uniquePlayers || 0,
          playerIds: dailyData.playerIds || [],
          avgGameDuration: dailyData.avgGameDuration || 0,
          totalGameDuration: dailyData.totalGameDuration || 0,
          hourlyBreakdown: dailyData.hourlyBreakdown || {},
          popularCategories: dailyData.popularCategories || {},
        };

        transaction.set(dailyRef, updates);
      });

      // Create game event record
      await setDoc(doc(db, "gameEvents", roomData.roomId), {
        roomId: roomData.roomId,
        roomName: roomData.roomName,
        timestamp: serverTimestamp(),
        createdAt: roomData.createdAt,
        playerCount: 1,
        hostId: roomData.hostId,
        customCategories: roomData.categories || null,
        isCustom: Boolean(
          roomData.categories &&
          JSON.stringify(roomData.categories) !==
          JSON.stringify(["name", "animal", "place", "thing"])
        ),
        status: "active",
        maxPlayers: roomData.maxPlayers || 3,
      });
    } catch (error) {
      console.error("Error tracking game creation:", error);
    }
  }

  // Track when a player joins
  async trackPlayerJoined(roomId, playerId, playerName) {
    try {
      const today = new Date().toISOString().split("T")[0];
      const hour = new Date().getHours();
      const month = new Date().toISOString().slice(0, 7);

      // Check if this is a returning player
      const isReturning = await this.checkReturningPlayer(playerId);

      // Update daily stats
      await runTransaction(db, async (transaction) => {
        const dailyRef = doc(db, "dailyStats", today);
        const dailyDoc = await transaction.get(dailyRef);
        let dailyData = dailyDoc.exists()
          ? dailyDoc.data()
          : await this.getOrCreateDailyStats(today);

        const playerIds = dailyData.playerIds || [];
        const isNewToday = !playerIds.includes(playerId);

        const updates = {
          [`hourlyBreakdown.${hour}.players`]: increment(1),
          lastUpdated: serverTimestamp(),
        };

        if (isNewToday) {
          updates.uniquePlayers = increment(1);
          updates.playerIds = arrayUnion(playerId);
        }

        transaction.update(dailyRef, updates);
      });

      // Update game event
      await updateDoc(doc(db, "gameEvents", roomId), {
        playerCount: increment(1),
        [`players.${playerId}`]: {
          name: playerName,
          joinedAt: serverTimestamp(),
          isReturning: isReturning,
        },
      });

      // Update monthly player stats
      await this.updateMonthlyPlayerStats(month, playerId, isReturning);
    } catch (error) {
      console.error("Error tracking player join:", error);
    }
  }

  // Track when a game ends
  async trackGameEnded(roomId, roomData, wasCompleted, endedBy = null) {
    try {
      const today = new Date().toISOString().split("T")[0];

      // Calculate game duration
      const gameStart = roomData.createdAt?.toDate
        ? roomData.createdAt.toDate()
        : new Date(roomData.createdAt);
      const duration = Math.floor((Date.now() - gameStart.getTime()) / 1000);

      // Update daily stats
      await runTransaction(db, async (transaction) => {
        const dailyRef = doc(db, "dailyStats", today);
        const dailyDoc = await transaction.get(dailyRef);
        let dailyData = dailyDoc.exists()
          ? dailyDoc.data()
          : await this.getOrCreateDailyStats(today);

        const currentConcurrent = Math.max(
          0,
          (dailyData.currentConcurrentGames || 1) - 1
        );
        const totalDuration = (dailyData.totalGameDuration || 0) + duration;
        const totalGames =
          (dailyData.gamesCompleted || 0) + (dailyData.gamesAbandoned || 0) + 1;

        transaction.update(dailyRef, {
          [wasCompleted ? "gamesCompleted" : "gamesAbandoned"]: increment(1),
          currentConcurrentGames: currentConcurrent,
          totalGameDuration: totalDuration,
          avgGameDuration: Math.floor(totalDuration / totalGames),
          lastUpdated: serverTimestamp(),
        });
      });

      // Update game event
      await updateDoc(doc(db, "gameEvents", roomId), {
        status: wasCompleted ? "completed" : "abandoned",
        completed: wasCompleted,
        duration: duration,
        roundsPlayed: roomData.currentRound || 0,
        endedAt: serverTimestamp(),
        endedBy: endedBy,
        finalPlayerCount: roomData.players?.length || 0,
      });

      // Track category usage if game was played
      if (roomData.currentRound > 0 && roomData.categories) {
        await this.trackCategoryUsage(roomData.categories);
      }
    } catch (error) {
      console.error("Error tracking game end:", error);
    }
  }

  // Fix concurrent game counts - call this periodically or when you notice issues
  async fixConcurrentGameCounts() {
    try {
      console.log("🔧 Fixing concurrent game counts...");

      // Get all active game events
      const activeGamesQuery = query(
        collection(db, "gameEvents"),
        where("status", "==", "active")
      );

      const activeGamesSnapshot = await getDocs(activeGamesQuery);
      const actualConcurrentCount = activeGamesSnapshot.size;

      console.log(`Found ${actualConcurrentCount} actually active games`);

      // Update today's stats with correct concurrent count
      const today = new Date().toISOString().split("T")[0];
      const dailyRef = doc(db, "dailyStats", today);

      await runTransaction(db, async (transaction) => {
        const dailyDoc = await transaction.get(dailyRef);

        if (dailyDoc.exists()) {
          transaction.update(dailyRef, {
            currentConcurrentGames: actualConcurrentCount,
            lastUpdated: serverTimestamp(),
          });
        }
      });

      console.log(`✅ Fixed concurrent count to ${actualConcurrentCount}`);
      return { success: true, correctedCount: actualConcurrentCount };
    } catch (error) {
      console.error("Error fixing concurrent game counts:", error);
      return { success: false, error: error.message };
    }
  }

  // Track custom category usage
  async trackCategoryUsage(categories) {
    try {
      const today = new Date().toISOString().split("T")[0];
      const month = new Date().toISOString().slice(0, 7);

      // Skip if default categories
      const isDefault =
        JSON.stringify(categories) ===
        JSON.stringify(["name", "animal", "place", "thing"]);
      if (isDefault) return;

      // Update daily popular categories
      await runTransaction(db, async (transaction) => {
        const dailyRef = doc(db, "dailyStats", today);
        const dailyDoc = await transaction.get(dailyRef);
        let dailyData = dailyDoc.exists()
          ? dailyDoc.data()
          : await this.getOrCreateDailyStats(today);

        const popularCategories = dailyData.popularCategories || {};

        categories.forEach((category) => {
          if (
            !["name", "animal", "place", "thing"].includes(
              category.toLowerCase()
            )
          ) {
            popularCategories[category] =
              (popularCategories[category] || 0) + 1;
          }
        });

        transaction.update(dailyRef, {
          popularCategories: popularCategories,
          lastUpdated: serverTimestamp(),
        });
      });

      // Update monthly category stats
      const categoryRef = doc(db, "categoryStats", month);
      const categoryDoc = await getDoc(categoryRef);

      if (categoryDoc.exists()) {
        const data = categoryDoc.data();
        const customCategories = data.customCategories || {};

        categories.forEach((category) => {
          if (
            !["name", "animal", "place", "thing"].includes(
              category.toLowerCase()
            )
          ) {
            customCategories[category] = (customCategories[category] || 0) + 1;
          }
        });

        await updateDoc(categoryRef, {
          customCategories: customCategories,
          lastUpdated: serverTimestamp(),
        });
      } else {
        const customCategories = {};
        categories.forEach((category) => {
          if (
            !["name", "animal", "place", "thing"].includes(
              category.toLowerCase()
            )
          ) {
            customCategories[category] = 1;
          }
        });

        await setDoc(categoryRef, {
          month: month,
          customCategories: customCategories,
          defaultVsCustom: { default: 0, custom: 1 },
          lastUpdated: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Error tracking category usage:", error);
    }
  }

  // Check if player is returning
  async checkReturningPlayer(playerId) {
    try {
      // Check cache first
      const cacheKey = `returning-${playerId}`;
      const cached = this.getCachedData(cacheKey);
      if (cached !== null) {
        return cached;
      }

      // Check last 30 days of player activity
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const playerQuery = query(
        collection(db, "gameEvents"),
        where(`players.${playerId}`, "!=", null),
        limit(1)
      );

      const snapshot = await getDocs(playerQuery);
      const isReturning = !snapshot.empty;

      // Cache the result for 1 hour (longer TTL for this)
      this.setCachedData(cacheKey, isReturning, 60 * 60 * 1000); // 1 hour

      return isReturning;
    } catch (error) {
      console.error("Error checking returning player:", error);
      return false;
    }
  }

  // Update monthly player statistics
  async updateMonthlyPlayerStats(month, playerId, isReturning) {
    try {
      const statsRef = doc(db, "playerStats", month);
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        const data = statsDoc.data();
        const playerIds = data.playerIds || [];
        const isNewThisMonth = !playerIds.includes(playerId);

        const updates = {
          lastUpdated: serverTimestamp(),
        };

        if (isNewThisMonth) {
          updates.totalUniquePlayers = increment(1);
          updates.playerIds = arrayUnion(playerId);
          if (isReturning) {
            updates.returningPlayers = increment(1);
          }
        }

        await updateDoc(statsRef, updates);
      } else {
        await setDoc(statsRef, {
          month: month,
          totalUniquePlayers: 1,
          returningPlayers: isReturning ? 1 : 0,
          playerIds: [playerId],
          lastUpdated: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Error updating monthly player stats:", error);
    }
  }

  // Get statistics for dashboard
  async getStatistics(timeRange = "week") {
    try {
      // Check cache first
      const cacheKey = `stats-${timeRange}`;
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        console.log(`📦 Using cached statistics for ${timeRange}`);
        return cached;
      }

      const stats = {
        today: {},
        week: {},
        month: {},
        allTime: {},
      };

      const today = new Date().toISOString().split("T")[0];
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);

      // Get today's stats
      const todayDoc = await getDoc(doc(db, "dailyStats", today));
      if (todayDoc.exists()) {
        stats.today = todayDoc.data();
      }

      // Get week stats
      const weekQuery = query(
        collection(db, "dailyStats"),
        where("date", ">=", weekAgo.toISOString().split("T")[0]),
        orderBy("date", "desc")
      );

      const weekSnapshot = await getDocs(weekQuery);
      stats.week = this.aggregateStats(
        weekSnapshot.docs.map((doc) => doc.data())
      );

      // Get month stats
      const monthQuery = query(
        collection(db, "dailyStats"),
        where("date", ">=", monthAgo.toISOString().split("T")[0]),
        orderBy("date", "desc")
      );

      const monthSnapshot = await getDocs(monthQuery);
      stats.month = this.aggregateStats(
        monthSnapshot.docs.map((doc) => doc.data())
      );

      // Get popular categories for current month
      const currentMonth = new Date().toISOString().slice(0, 7);
      const categoryDoc = await getDoc(doc(db, "categoryStats", currentMonth));
      if (categoryDoc.exists()) {
        stats.popularCategories = categoryDoc.data().customCategories || {};
      }

      // Cache the results
      this.setCachedData(cacheKey, stats);

      return stats;
    } catch (error) {
      console.error("Error getting statistics:", error);
      return null;
    }
  }

  // Aggregate stats helper
  // Replace the aggregateStats method in statisticsService.js
  aggregateStats(dailyStats) {
    const aggregated = {
      gamesCreated: 0,
      gamesCompleted: 0,
      gamesAbandoned: 0,
      uniquePlayers: new Set(),
      avgGameDuration: 0,
      peakConcurrentGames: 0,
      customCategoryGames: 0,
      defaultCategoryGames: 0,
      popularHours: {},
      dailyData: [],
    };

    let totalDuration = 0;
    let completedGameCount = 0; // Only count games that actually completed

    // Initialize all hours to prevent gaps
    for (let i = 0; i < 24; i++) {
      aggregated.popularHours[i] = { games: 0, players: 0 };
    }

    dailyStats.forEach((day) => {
      aggregated.gamesCreated += day.gamesCreated || 0;
      aggregated.gamesCompleted += day.gamesCompleted || 0;
      aggregated.gamesAbandoned += day.gamesAbandoned || 0;
      aggregated.customCategoryGames += day.customCategoryGames || 0;
      aggregated.defaultCategoryGames += day.defaultCategoryGames || 0;
      aggregated.peakConcurrentGames = Math.max(
        aggregated.peakConcurrentGames,
        day.peakConcurrentGames || 0
      );

      // Properly aggregate unique players
      if (day.playerIds && Array.isArray(day.playerIds)) {
        day.playerIds.forEach((id) => aggregated.uniquePlayers.add(id));
      }

      // Only add duration from completed games
      if (day.gamesCompleted > 0 && day.totalGameDuration) {
        totalDuration += day.totalGameDuration;
        completedGameCount += day.gamesCompleted;
      }

      // Aggregate hourly data properly
      if (day.hourlyBreakdown) {
        Object.entries(day.hourlyBreakdown).forEach(([hour, data]) => {
          if (aggregated.popularHours[hour]) {
            aggregated.popularHours[hour].games += data.games || 0;
            aggregated.popularHours[hour].players += data.players || 0;
          }
        });
      }

      // Keep daily data for charts
      aggregated.dailyData.push({
        date: day.date,
        games: day.gamesCreated || 0,
        completed: day.gamesCompleted || 0,
        players: day.uniquePlayers || 0,
      });
    });

    aggregated.uniquePlayers = aggregated.uniquePlayers.size;
    aggregated.avgGameDuration =
      completedGameCount > 0
        ? Math.floor(totalDuration / completedGameCount)
        : 0;

    // Fix completion rate calculation - only count finished games
    const finishedGames = aggregated.gamesCompleted + aggregated.gamesAbandoned;
    aggregated.completionRate =
      finishedGames > 0
        ? Math.round((aggregated.gamesCompleted / finishedGames) * 100)
        : 0;

    // Find most popular hour
    let maxGames = 0;
    let popularHour = 12;
    Object.entries(aggregated.popularHours).forEach(([hour, data]) => {
      if (data.games > maxGames) {
        maxGames = data.games;
        popularHour = parseInt(hour);
      }
    });
    aggregated.peakHour = `${popularHour}:00`;

    return aggregated;
  }
}

export const statsService = new StatisticsService();
